import { API } from "./APIClient";
import {
    getCurrentUserId,
    getCurrentUserName,
} from "../utils/reduxUser";

class UserAPIClass {
    async resolveCurrentUserId(userId = null) {
        return userId || getCurrentUserId();
    }

    async fetchUserInfo() {
        const response = await API.get(`/users/userInfo`);

        const mappedUser = {
            userId: response.data.user_id,
            name: response.data.name,
            cafeId: response.data.cafe_id,
            level: response.data.level,
            totalExp: response.data.total_exp,
            kpiSchemeId: response.data.kpi_scheme_id,
            email: response.data.email,
            isActive: response.data.is_active,
            is2faEnabled: response.data.is_2fa_enabled,
            hasCredentials: response.data.has_credentials,
            profilePhotoUrl: response.data.profile_photo_url,
        };

        return mappedUser;
    }

    async fetchUserStatus() {
        const response = await API.get(`/users/userStatus`);

        return {
            isUser: response.data.is_emp,
            isAdmin: response.data.is_admin,
            isSuperAdmin: response.data.is_sa,
        };
    }

    async fetchUserAchieves(userId = null) {
        const resolvedUserId = await this.resolveCurrentUserId(userId);
        const response = await API.get(`/users/userAchieves/${resolvedUserId}`);

        return response.data.achievements;
    }

    async updateUserInfo(userId, { cafeId, level, totalExp, kpiSchemeId, name }) {
        const response = await API.patch("/users/updateInfo", {
            user_id: userId,
            cafe_id: cafeId,
            level,
            total_exp: totalExp,
            kpi_scheme_id: kpiSchemeId,
            name,
            position: "",
            earnings: 0,
        });

        return response.data;
    }

    async createUser(name = null, userId = null) {
        const resolvedUserId = await this.resolveCurrentUserId(userId);
        const userName = name || getCurrentUserName() || "Пользователь";

        const response = await API.post("/users/createUser", {
            user_id: resolvedUserId,
            name: userName,
        });

        return response.data;
    }

    async updateExp(exp_count) {
        const response = await API.patch("/users/updateExp", { exp_count });

        return {
            newExp: response.data.result.new_exp_count,
            newLevel: response.data.result.new_level_status,
        };
    }

    async updateLevel(new_level) {
        const response = await API.post("/users/updateLevel", { new_level });

        return response.data;
    }

    async fetchDocsAgreement(userId = null) {
        const resolvedUserId = await this.resolveCurrentUserId(userId);
        const response = await API.get(`/users/user/${resolvedUserId}/checkDocsAgreement`);

        return response.data.is_agreed;
    }

    async updateDocsAgreement(userId = null) {
        const resolvedUserId = await this.resolveCurrentUserId(userId);
        const response = await API.patch(`/users/user/${resolvedUserId}/updateDocsAgreement`);

        return response.data.is_agreed;
    }

    async updatePassword(currentPassword, newPassword, { challengeId = null, otpCode = null } = {}) {
        const payload = {
            current_password: currentPassword,
            new_password: newPassword,
        };

        if (challengeId) {
            payload.challenge_id = challengeId;
        }

        if (otpCode) {
            payload.otp_code = otpCode;
        }

        const response = await API.patch("/users/updatePassword", payload);

        return response.data;
    }

    async requestUpdatePasswordOtp(currentPassword) {
        const response = await API.post("/users/updatePassword/requestOtp", {
            current_password: currentPassword,
        });

        return {
            requiresOtp: Boolean(response.data.requires_otp),
            challengeId: response.data.challenge_id || null,
            expiresIn: response.data.expires_in || null,
            message: response.data.message || null,
        };
    }

    async fetchFAQ(cafeId, userId = null) {
        const resolvedUserId = await this.resolveCurrentUserId(userId);
        const response = await API.get(`/cafe/${cafeId}/faq?employee_id=${resolvedUserId}`);

        const result = [];
        for (const item of response.data.items) {
            result.push({
                id: item.id,
                title: item.question_text,
                description: item.question_answer,
            });
        }

        return result;
    }

    async uploadProfilePhoto(imageFile) {
        const formData = new FormData();
        formData.append("photo", imageFile);

        const response = await API.post("/users/profilePhoto", formData);

        return response.data.profile_photo_url;
    }

    async deleteProfilePhoto() {
        const response = await API.delete("/users/profilePhoto");
        return response.data;
    }
}

const UserAPI = new UserAPIClass();
export default UserAPI;
